import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import ReactConfetti from 'react-confetti';
import SuperAdminSidebar from '../../components/Layout/SuperAdminSidebar';
import superAdminService from '../../services/superAdminService';
import authService from '../../services/authService';

// Material UI components
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  InputAdornment,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Snackbar,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery
} from '@mui/material';

// Material UI icons
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Help as HelpIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const SuperAdminAddTenantPage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openInfoCard, setOpenInfoCard] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user || user.role !== 'superadmin') {
          setError('You must be logged in as a superadmin to access this page.');
          setAuthChecked(false);
        } else {
          setAuthChecked(true);
        }
      } catch (err) {
        console.error("Auth check error:", err);
        setError('Authentication error. Please try logging in again.');
      }
    };
    
    checkAuth();
  }, []);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    setError: setFormError,
    clearErrors,
    trigger
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      subdomain: '',
      adminEmail: '',
      adminPassword: '',
      confirmPassword: '',
      adminFirstName: '',
      adminLastName: '',
      phone: '',
      address: '',
      website: '',
      planId: 'basic',
      status: 'active',
      sendWelcomeEmail: true
    }
  });
  
  const watchSubdomain = watch('subdomain');
  const watchPassword = watch('adminPassword');
  
  // Steps for the stepper
  const steps = [
    {
      label: 'Organization Details',
      description: 'Enter basic information about the organization',
    },
    {
      label: 'Admin Account',
      description: 'Set up the primary administrator',
    },
    {
      label: 'Plan & Settings',
      description: 'Choose a subscription plan and configure settings',
    },
  ];
  
  // Handle stepper next button
  const handleNext = async () => {
    // Validate current step fields
    let isStepValid = false;
    
    if (activeStep === 0) {
      // Validate organization details
      const result = await trigger(['name', 'subdomain']);
      isStepValid = result && subdomainAvailable;
    } else if (activeStep === 1) {
      // Validate admin account
      isStepValid = await trigger(['adminFirstName', 'adminLastName', 'adminEmail', 'adminPassword', 'confirmPassword']);
    } else {
      // Plan & Settings step - always valid
      isStepValid = true;
    }
    
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };
  
  // Handle stepper back button
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  // Check subdomain availability
  const checkSubdomain = async () => {
    if (!watchSubdomain || watchSubdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    
    try {
      setCheckingSubdomain(true);
      const result = await superAdminService.checkSubdomainAvailability(watchSubdomain);
      setSubdomainAvailable(result.available);
      
      if (!result.available) {
        setFormError('subdomain', {
          type: 'manual',
          message: 'This subdomain is already taken'
        });
      } else {
        clearErrors('subdomain');
      }
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };
  
  // Format subdomain as user types
  const handleSubdomainChange = (e) => {
    // Convert to lowercase and remove spaces and special characters
    const formatted = e.target.value.toLowerCase()
      .replace(/[^a-z0-9-]/g, '')  // Only allow lowercase letters, numbers, and hyphens
      .replace(/^-+|-+$/g, '');     // Remove leading and trailing hyphens
    
    setValue('subdomain', formatted);
    
    if (formatted && formatted.length >= 3) {
      // Check availability after a short delay
      const timer = setTimeout(() => {
        checkSubdomain();
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      setSubdomainAvailable(null);
    }
  };
  
  // Toggle password visibility
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  // Submit form
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Create tenant data object
      const { confirmPassword, ...tenantData } = data;
      
      // Create proper admin user object with all required fields
      tenantData.admin = {
        firstName: data.adminFirstName,
        lastName: data.adminLastName,
        email: data.adminEmail,
        password: data.adminPassword,
        role: 'admin',
        // Status matches tenant status
        status: data.status || 'active'
      };
      
      // Format plan data correctly to match Tenant model enum values
      tenantData.plan = data.planId;
      
      // Set subdomain as the primary identifier
      tenantData.subdomain = data.subdomain;
      tenantData.domain = data.subdomain;
      tenantData.adminEmail = data.adminEmail;
      
      // Make sure we're setting the sendWelcomeEmail flag
      tenantData.sendWelcomeEmail = data.sendWelcomeEmail;
      
      console.log('Creating tenant with data:', tenantData);
      
      try {
        // Call API
        const response = await superAdminService.createTenant(tenantData);
        
        console.log('Tenant created successfully:', response);
        setSuccessMessage(`Organization "${tenantData.name}" created successfully`);
        setShowConfetti(true);
        
        // Navigate to tenant list with success message after a brief delay
        setTimeout(() => {
          navigate('/superadmin/tenants', { 
            state: { 
              successMessage: `Organization "${tenantData.name}" created successfully`
            }
          });
        }, 1500);
      } catch (apiError) {
        console.error('API Error:', apiError);
        
        // Enhanced error handling
        if (apiError.response) {
          const status = apiError.response.status;
          const data = apiError.response.data;
          
          if (status === 409) {  // Conflict - duplicate key
            // Try to extract the specific field causing the conflict
            const message = data.message || 'A duplicate value was detected';
            
            // Check if message contains information about the duplicate field
            if (message.includes('subdomain')) {
              setFormError('subdomain', {
                type: 'manual',
                message: 'This subdomain is already in use. Please choose a different one.'
              });
              setSubdomainAvailable(false);
              setError('This subdomain is already in use by another organization. Please try a different one.');
            } else if (message.includes('email') || message.includes('adminEmail')) {
              setFormError('adminEmail', {
                type: 'manual',
                message: 'This email is already registered. Please use a different one.'
              });
              setError('This admin email is already in use. Please use a different email address.');
            } else {
              // Generic duplicate error
              setError(message);
            }
            
            // Re-check subdomain availability to update UI state
            if (data.subdomain) {
              checkSubdomain();
            }
          } else if (status === 400) {  // Bad request - validation error
            setError(data.message || 'Please check your input and try again.');
          } else {
            // Other errors
            setError(data.message || apiError.message || 'Failed to create organization');
          }
        } else if (apiError.message?.includes('must be logged in as a superadmin')) {
          setError('Authentication error: You must be logged in as a superadmin. Please log out and log back in.');
        } else {
          setError(apiError.message || 'An error occurred while creating the organization');
        }
      }
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Get plan name from ID - Updated to match Tenant model enum values
  const getPlanName = (planId) => {
    const plans = {
      basic: 'Basic Plan',
      professional: 'Professional Plan',
      enterprise: 'Enterprise Plan',
      custom: 'Custom Plan'
    };
    
    return plans[planId] || 'Basic Plan';
  };

  // Get plan description
  const getPlanDescription = (planId) => {
    const descriptions = {
      basic: 'Essential features for small organizations.',
      professional: 'Advanced features with priority support.',
      enterprise: 'Complete solution for large organizations.',
      custom: 'Tailored solution for your specific needs.'
    };
    
    return descriptions[planId] || '';
  };
  
  // Get plan colors for consistent styling
  const getPlanColors = (planId) => {
    const colors = {
      basic: {
        main: '#4caf50', // Green
        light: 'rgba(76, 175, 80, 0.1)',
        border: 'rgba(76, 175, 80, 0.5)'
      },
      professional: {
        main: '#2196f3', // Blue
        light: 'rgba(33, 150, 243, 0.1)',
        border: 'rgba(33, 150, 243, 0.5)'
      },
      enterprise: {
        main: '#9c27b0', // Purple
        light: 'rgba(156, 39, 176, 0.1)',
        border: 'rgba(156, 39, 176, 0.5)'
      },
      custom: {
        main: '#ff9800', // Orange
        light: 'rgba(255, 152, 0, 0.1)',
        border: 'rgba(255, 152, 0, 0.5)'
      }
    };
    
    return colors[planId] || colors.basic;
  };
  
  return (
    <Container maxWidth="xl">
      {/* Page Header */}
      <Box my={4}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            to="/superadmin/tenants"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Organizations
          </Link>
          <Typography color="text.primary">Add New Organization</Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Create New Organization
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Set up a new tenant organization and admin user
        </Typography>
      </Box>
      
      {/* Alert for errors or success */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setSuccessMessage('')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}
      
      {/* Info Card */}
      <Collapse in={openInfoCard}>
        <Card 
          sx={{ 
            mb: 4, 
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.contrastText
          }}
        >
          <CardContent sx={{ position: 'relative' }}>
            <IconButton 
              size="small" 
              sx={{ position: 'absolute', right: 8, top: 8 }}
              onClick={() => setOpenInfoCard(false)}
              color="inherit"
              aria-label="Close info card"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <InfoIcon sx={{ mr: 2, mt: 0.5 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Creating a New Organization
                </Typography>
                <Typography variant="body2">
                  Follow the steps below to create a new organization. You'll need to provide organization details,
                  create an admin account, and select a subscription plan. The subdomain will be used for accessing
                  the organization's portal.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Collapse>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper
          }}
        >
          {/* Stepper */}
          <Stepper 
            activeStep={activeStep} 
            orientation={isSmallScreen ? "vertical" : "horizontal"}
            sx={{ mb: 4 }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                {isSmallScreen && <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepContent>}
              </Step>
            ))}
          </Stepper>
          
          {/* Step Content */}
          <Box sx={{ mt: 4, mb: 3 }}>
            {activeStep === 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1, color: theme.palette.primary.main }} /> 
                  Organization Information
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Enter the basic information about the organization you're creating.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Organization Name"
                      {...register('name', { 
                        required: 'Organization name is required',
                        minLength: {
                          value: 2,
                          message: 'Name must be at least 2 characters'
                        }
                      })}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subdomain"
                      {...register('subdomain', { 
                        required: 'Subdomain is required',
                        minLength: {
                          value: 3,
                          message: 'Subdomain must be at least 3 characters'
                        },
                        pattern: {
                          value: /^[a-z0-9-]+$/,
                          message: 'Only lowercase letters, numbers, and hyphens are allowed'
                        }
                      })}
                      onChange={handleSubdomainChange}
                      error={!!errors.subdomain}
                      helperText={errors.subdomain?.message || (
                        watchSubdomain && watchSubdomain.length >= 3 ? 
                          subdomainAvailable === true ? 
                            'Subdomain is available' : 
                          subdomainAvailable === false ? 
                            'Subdomain is already taken' : 
                            'Checking availability...' : 
                          'Subdomain must be at least 3 characters'
                      )}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: watchSubdomain && watchSubdomain.length >= 3 ? (
                          <InputAdornment position="end">
                            {checkingSubdomain ? (
                              <CircularProgress size={20} />
                            ) : subdomainAvailable === true ? (
                              <Tooltip title="Subdomain is available">
                                <CheckIcon color="success" />
                              </Tooltip>
                            ) : subdomainAvailable === false ? (
                              <Tooltip title="Subdomain is already taken">
                                <Chip 
                                  label="Unavailable" 
                                  size="small" 
                                  color="error" 
                                  variant="outlined" 
                                />
                              </Tooltip>
                            ) : null}
                          </InputAdornment>
                        ) : null
                      }}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      {...register('phone')}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      {...register('website')}
                      error={!!errors.website}
                      helperText={errors.website?.message}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={2}
                      {...register('address')}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <LocationIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            
            {activeStep === 1 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} /> 
                  Admin Account
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Create the primary administrator account for this organization.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      {...register('adminFirstName', { 
                        required: 'First name is required' 
                      })}
                      error={!!errors.adminFirstName}
                      helperText={errors.adminFirstName?.message}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      {...register('adminLastName', { 
                        required: 'Last name is required' 
                      })}
                      error={!!errors.adminLastName}
                      helperText={errors.adminLastName?.message}
                      variant="outlined"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      {...register('adminEmail', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      error={!!errors.adminEmail}
                      helperText={errors.adminEmail?.message}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      {...register('adminPassword', { 
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters'
                        }
                      })}
                      error={!!errors.adminPassword}
                      helperText={errors.adminPassword?.message || "Password must be at least 8 characters"}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type={showPassword ? "text" : "password"}
                      {...register('confirmPassword', { 
                        required: 'Please confirm password',
                        validate: value => value === watchPassword || 'Passwords do not match'
                      })}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </>
            )}
            
            {activeStep === 2 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1, color: theme.palette.primary.main }} /> 
                  Plan & Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Choose a subscription plan and configure settings for this organization.
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Select Subscription Plan
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                      {['basic', 'professional', 'enterprise', 'custom'].map((plan) => {
                        const planColors = getPlanColors(plan);
                        const isSelected = watch('planId') === plan;
                        
                        return (
                          <Card 
                            key={plan}
                            sx={{ 
                              flex: 1, 
                              cursor: 'pointer',
                              border: isSelected ? `2px solid ${planColors.main}` : '1px solid rgba(0, 0, 0, 0.12)',
                              backgroundColor: isSelected ? planColors.light : 'inherit',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              overflow: 'visible',
                              '&:hover': {
                                boxShadow: 3,
                                borderColor: planColors.border,
                                backgroundColor: isSelected ? planColors.light : 'rgba(0, 0, 0, 0.02)'
                              }
                            }}
                            onClick={() => setValue('planId', plan)}
                          >
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -10,
                                  right: -10,
                                  backgroundColor: planColors.main,
                                  borderRadius: '50%',
                                  width: 24,
                                  height: 24,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                              </Box>
                            )}
                            <CardHeader
                              title={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: planColors.main,
                                      mr: 1
                                    }}
                                  />
                                  {getPlanName(plan)}
                                </Box>
                              }
                              titleTypographyProps={{ 
                                variant: 'h6',
                                sx: { color: isSelected ? planColors.main : 'inherit' } 
                              }}
                              sx={{ pb: 0 }}
                            />
                            <CardContent>
                              <Typography variant="body2" color="text.secondary">
                                {getPlanDescription(plan)}
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="status-label">Initial Status</InputLabel>
                      <Select
                        labelId="status-label"
                        {...register('status')}
                        defaultValue="active"
                        label="Initial Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          defaultChecked
                          {...register('sendWelcomeEmail')}
                        />
                      }
                      label="Send welcome email to admin"
                    />
                    <Typography variant="body2" color="text.secondary">
                      The admin will receive an email with login instructions
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
            
            {activeStep === steps.length && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                  Ready to create organization
                </Typography>
                <Typography variant="body1" paragraph>
                  Please review all information before submitting.
                </Typography>
                <Alert severity="info" sx={{ mb: 3, mx: 'auto', maxWidth: 500 }}>
                  Once created, the organization and admin user will be immediately available.
                </Alert>
              </Box>
            )}
          </Box>
          
          {/* Form Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
            <Button
              color="inherit"
              variant="text"
              onClick={activeStep === 0 ? () => navigate('/superadmin/tenants') : handleBack}
              startIcon={activeStep === 0 ? <ArrowBackIcon /> : undefined}
              size="medium"
            >
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Box>
              {activeStep === steps.length ? (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                  size="medium"
                  sx={{
                    background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                    boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                  }}
                >
                  {loading ? 'Creating...' : 'Create Organization'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  size="medium"
                  endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />}
                >
                  {activeStep === steps.length - 1 ? 'Review' : 'Continue'}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </form>
      
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message="Some notification message"
      />

      {showConfetti && <ReactConfetti />}
    </Container>
  );
};

export default SuperAdminAddTenantPage;